import { useEffect, useState } from 'react';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useSocketChannel } from 'services/api/updatesSocket';
import { fetchData } from 'services/api/fetchData';
import { saveData } from 'services/api/saveData';
import { QueryParamType } from 'types/ListTypes';
import { BulkActionKind, BulkActionStatus, BulkExportLevel, BulkSelectionState } from 'types/BulkActionTypes';
import { buildBulkActionSelectionPayload } from 'components/lists/functions/selectionUtils';

/*
 * useBulkActionRunner.ts
 * Hook that starts a bulk action (delete/deactivate, export or a custom
 * action) and then tracks its progress, mirroring the websocket plus
 * 5-second polling fallback pattern used by ImportWizard.tsx.
 */

interface StartBulkActionParams {
    kind: BulkActionKind;
    filters: QueryParamType;
    selection: BulkSelectionState;
    exportFormat?: 'xlsx' | 'csv';
    exportLevel?: BulkExportLevel;
    actionKey?: string;
}

export function useBulkActionRunner(objectKey: string) {
    const { handleLogout } = useAuthContext();
    const { message: environmentWebsocket } = useSocketChannel('websockets', ['data_import_progress', 'bulk_action_progress']);

    // Track the started bulk action and its reported progress
    const [bulkActionId, setBulkActionId] = useState<number | null>(null);
    const [status, setStatus] = useState<BulkActionStatus | null>(null);
    const [progress, setProgress] = useState<number>(0);
    const [totalRows, setTotalRows] = useState<number>(0);
    const [processedRows, setProcessedRows] = useState<number>(0);
    const [resultSummary, setResultSummary] = useState<Record<string, any> | null>(null);
    const [resultFileUrl, setResultFileUrl] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [starting, setStarting] = useState<boolean>(false);

    // Receive websocket updates for this bulk action while it is running
    useEffect(() => {
        if (!bulkActionId || !environmentWebsocket) {
            return;
        }

        const { type, bulk_action_id, progress: wsProgress, status: wsStatus, result_summary, result_file_url, error_message } = environmentWebsocket;

        // Only handle progress messages that belong to this bulk action
        if (type === 'bulk_action_progress' && bulk_action_id === bulkActionId) {
            setProgress(wsProgress);
            setStatus(wsStatus);

            // The result fields are only populated in the final message
            if (result_summary != null) {
                setResultSummary(result_summary);
            }
            if (result_file_url) {
                setResultFileUrl(result_file_url);
            }

            // The error message is only populated in the final failed message
            if (error_message) {
                setErrorMessage(error_message);
            }
        }
    }, [environmentWebsocket, bulkActionId]);

    // Poll the bulk action status every 5 seconds as a fallback for the websocket
    useEffect(() => {
        let interval: NodeJS.Timeout | null = null;

        const pollBulkActionStatus = async () => {
            if (!bulkActionId) {
                return;
            }

            try {
                const responseData = await fetchData({ apiUrl: `get_bulk_action/${bulkActionId}`, handleLogout });

                if (responseData) {
                    setStatus(responseData.status);
                    setProgress(responseData.progress);
                    setTotalRows(responseData.total_rows);
                    setProcessedRows(responseData.processed_rows);
                    setResultSummary(responseData.result_summary);
                    setResultFileUrl(responseData.result_file_url);
                    setErrorMessage(responseData.error_message);

                    // Stop polling once the bulk action reached a final status
                    if (responseData.status !== 'in_progress' && interval) {
                        clearInterval(interval);
                    }
                }
            } catch (error) {
                console.error('Error fetching bulk action status', error);
            }
        };

        // Only poll while the bulk action is actually running
        if (status === 'in_progress' && bulkActionId) {
            interval = setInterval(pollBulkActionStatus, 5000);
        }

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [status, bulkActionId]);

    // Reset the runner state so the panel can start a fresh bulk action
    const reset = () => {
        setBulkActionId(null);
        setStatus(null);
        setProgress(0);
        setTotalRows(0);
        setProcessedRows(0);
        setResultSummary(null);
        setResultFileUrl(null);
        setErrorMessage(null);
    };

    // Start the bulk action, calls the endpoint that matches the given kind
    // Returns whether the bulk action was actually started, so the panel
    // knows whether it may move on to the running step
    const start = async ({ kind, filters, selection, exportFormat, exportLevel, actionKey }: StartBulkActionParams): Promise<boolean> => {
        setStarting(true);
        setErrorMessage(null);

        try {
            // Configure the shared part of the request body for every bulk action
            const basePayload = {
                object: objectKey,
                filters,
                ...buildBulkActionSelectionPayload(selection),
            };

            // Pick the endpoint and extra body fields based on the action kind
            let response;
            if (kind === 'export') {
                response = await saveData({ apiUrl: 'post_bulk_export', method: 'post', data: { ...basePayload, format: exportFormat, export_level: exportLevel ?? 'document' } });
            } else if (kind === 'custom') {
                response = await saveData({ apiUrl: 'post_bulk_custom_action', method: 'post', data: { ...basePayload, action_key: actionKey } });
            } else if (kind === 'restore') {
                response = await saveData({ apiUrl: 'post_bulk_delete_or_deactivate', method: 'post', data: { ...basePayload, restore: true } });
            } else {
                response = await saveData({ apiUrl: 'post_bulk_delete_or_deactivate', method: 'post', data: basePayload });
            }

            // Start tracking the created bulk action on a successful response
            if (response && response.status === 201 && response.data.bulk_action_id) {
                setBulkActionId(response.data.bulk_action_id);
                setStatus('in_progress');
                return true;
            }

            setErrorMessage('bulk_actions.general.start_error_message');
            return false;
        } catch (error) {
            console.error('Error starting bulk action', error);
            setErrorMessage('bulk_actions.general.start_error_message');
            return false;
        } finally {
            setStarting(false);
        }
    };

    return {
        bulkActionId, status, progress, totalRows, processedRows,
        resultSummary, resultFileUrl, errorMessage, starting, start, reset,
    };
}
